import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { type FunctionComponent, useEffect, useMemo } from "react";

import { defaultAdditionalMetaTags, PageSeo } from "~/components/meta";
import { trackingAtom } from "~/components/tracking";
import { Edition, editionMetaDescription } from "~/lib/edition";
import { useCanonicalUrl } from "~/lib/hooks";

type Props = {
  edition: Edition;
};

export const HomeSeo: FunctionComponent<Props> = ({ edition }) => {
  const editionTitle = useMemo(() => {
    switch (edition) {
      case Edition.Asia:
        return "Breaking Asia, China, HK News, Opinions and Insights";
      case Edition.International:
        return "Breaking China, Asia, HK News, Opinions and Insights";
      default:
        return "Breaking HK, China and World News, Opinions and Insights";
    }
  }, [edition]);

  const editionDescription = editionMetaDescription[edition];

  const { canonicalUrl } = useCanonicalUrl(`/${edition === Edition.International ? "" : edition}`);

  const { asPath } = useRouter();
  const { trackPageView } = useAtomValue(trackingAtom);
  useEffect(() => {
    trackPageView?.({
      contentType: "Homepage",
      edition,
      page: "Home",
      path: asPath,
      title: editionTitle,
    });
  }, [trackPageView, edition, editionTitle, asPath]);

  return (
    <PageSeo
      additionalMetaTags={[
        ...defaultAdditionalMetaTags,
        {
          content: "6860eccecbe3d7c71bd25fe2e28701b3",
          name: "p:domain_verify",
        },
      ]}
      canonical={canonicalUrl}
      description={editionDescription}
      languageAlternates={[
        {
          href: "https://www.scmp.com/hk",
          hrefLang: "en-hk",
        },
        {
          href: "https://www.scmp.com/asia",
          hrefLang: "en",
        },
        {
          href: "https://www.scmp.com",
          hrefLang: "x-default",
        },
      ]}
      title={editionTitle}
      twitter={{
        cardType: "summary",
      }}
    />
  );
};

HomeSeo.displayName = "HomeSeo";
